import { useState, useRef } from 'react';
import { type QueryLazyOptions, useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { QueryVariables, FetchConfigData } from '../../common/types/index.tsx';
import {
	transformQueryResults,
	tranformSubtaskData,
} from '../../common/utils/fetch-config-query/index.tsx';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml/index.tsx';
import { useModalLoadUiTaskEvents } from '../../../issue-create-analytics/src/controllers/use-modal-load-ui-task-events/index.tsx';
import type { CreateSubtaskConfigQuery } from '../../common/types/create-config-query.tsx';
import { getCreateSubtaskConfigUserPreferredQuery } from './gql.tsx';
import type { UseFetchSubtaskCreateConfigQuery } from './types.tsx';

const useFetchSubtaskCreateConfigQuery: UseFetchSubtaskCreateConfigQuery = (props = {}) => {
	const { fireModalLoadTaskFailEvent, fireModalLoadTaskAbortEvent } = useModalLoadUiTaskEvents();
	const { displayOnlyRequiredFields = false, triggerPointKey } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = useSmartFieldsMachineLearning();

	const [fetchSubtaskConfig] = useLazyQuery<CreateSubtaskConfigQuery, QueryVariables>(
		getCreateSubtaskConfigUserPreferredQuery(withMachineLearning),
		{
			fetchPolicy: 'no-cache',
			onCompleted: (queryData) => {
				setApiLatency(Math.floor(performance.now() - apiStartTime.current));
				setData(
					// todo: change typing for setData and propogate strict typing through the rest of the codebase
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					transformQueryResults(
						tranformSubtaskData(queryData),
						displayOnlyRequiredFields,
					) as FetchConfigData,
				);
				setLoading(false);
			},
			onError: (error: ApolloError) => {
				setFetchError(error);
				const errorType = handleApolloError(error);
				fireErrorAnalytics({
					meta: {
						id: 'useFetchSubtaskCreateConfigQuery',
						packageName: 'jiraIssueCreate',
						teamName: 'gryffindor',
					},
					error,
					attributes: {
						// TODO: look to get this into common hook and populate it everywhere.
						triggerPointKey,
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				if (errorType === 'graphQLError' && fg('global_issue_create_modal_view_task_fail_event')) {
					fireModalLoadTaskFailEvent();
				} else if (fg('global_issue_create_modal_view_task_abort_event')) {
					fireModalLoadTaskAbortEvent();
				}
			},
		},
	);
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchSubtaskConfig(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchSubtaskCreateConfigQuery;
